import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

const ApiDebugView = () => {
  const [debugInfo, setDebugInfo] = useState(null);
  const [endpointTests, setEndpointTests] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testInProgress, setTestInProgress] = useState(false);

  useEffect(() => {
    fetchDebugInfo();
  }, []);

  const fetchDebugInfo = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_URL}/debug`, { timeout: 5000 });
      setDebugInfo(response.data);
      
      // Auto-run tests if debug info is available
      if (response.data) {
        runEndpointTests();
      }
    } catch (err) {
      console.error('Error fetching API debug info:', err);
      setError('Could not connect to API. Check the API URL configuration.');
      
      // Still try to run endpoint tests
      runEndpointTests();
    } finally {
      setLoading(false);
    }
  };

  const runEndpointTests = async () => {
    setTestInProgress(true);
    
    const endpoints = [
      { path: '/meals', method: 'GET' },
      { path: '/mealplans', method: 'GET' },
      { path: '/shopping-list', method: 'GET' },
      { path: '/ingredients', method: 'GET' },
      { path: '/debug', method: 'GET' }
    ];
    
    const results = {};
    
    for (const { path, method } of endpoints) {
      try {
        const start = Date.now();
        const response = await axios({
          method,
          url: `${API_URL}${path}`,
          timeout: 5000,
          validateStatus: () => true // Accept any status code
        });
        const duration = Date.now() - start;
        
        const corsHeader = response.headers && 
          (response.headers['access-control-allow-origin'] || 
           response.headers['Access-Control-Allow-Origin']);
        
        results[path] = {
          status: response.status,
          duration: `${duration}ms`,
          corsEnabled: !!corsHeader,
          corsHeader,
          data: response.data ? truncateData(response.data) : null,
          error: null,
          timestamp: new Date().toISOString()
        };
      } catch (err) {
        results[path] = {
          status: 'Error',
          duration: 'N/A',
          corsEnabled: false,
          corsHeader: null,
          data: null,
          error: err.message,
          timestamp: new Date().toISOString()
        };
      }
    }
    
    setEndpointTests(results);
    setTestInProgress(false);
  };
  
  const truncateData = (data) => {
    const str = JSON.stringify(data);
    if (str.length > 100) {
      return str.substring(0, 100) + '...';
    }
    return str;
  };

  return (
    <div className="api-debug-view container mt-4">
      <div className="card">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">API Connection Diagnostics</h5>
          <button 
            className="btn btn-sm btn-light" 
            onClick={fetchDebugInfo}
            disabled={loading || testInProgress}
          >
            {loading ? 'Loading...' : testInProgress ? 'Testing...' : 'Run Tests'}
          </button>
        </div>
        
        <div className="card-body">
          <div className="alert alert-info">
            <strong>Current API URL:</strong> <code>{API_URL}</code>
          </div>
          
          {error && (
            <div className="alert alert-danger">
              <strong>Connection Error:</strong> {error}
            </div>
          )}
          
          {debugInfo && (
            <div className="mb-4">
              <h6>API Status</h6>
              <div className="alert alert-success">
                <p>
                  <strong>Status:</strong> {debugInfo.status}<br />
                  <strong>Version:</strong> {debugInfo.worker_version}<br />
                  <strong>Timestamp:</strong> {debugInfo.timestamp}
                </p>
              </div>
            </div>
          )}
          
          <h6>Endpoint Tests</h6>
          {testInProgress ? (
            <div className="text-center my-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Testing API endpoints...</p>
            </div>
          ) : (
            <table className="table table-sm table-striped">
              <thead>
                <tr>
                  <th>Endpoint</th>
                  <th>Status</th>
                  <th>Response Time</th>
                  <th>CORS</th>
                  <th>Data Sample</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(endpointTests).map(([path, info]) => (
                  <tr key={path} className={info.status === 200 ? 'table-success' : 'table-warning'}>
                    <td><code>{path}</code></td>
                    <td>
                      <span className={info.status === 200 ? 'text-success' : 'text-danger'}>
                        {info.status}
                      </span>
                    </td>
                    <td>{info.duration}</td>
                    <td>{info.corsEnabled ? '✅' : '❌'}</td>
                    <td>
                      {info.error ? (
                        <span className="text-danger">{info.error}</span>
                      ) : (
                        <small><code>{info.data}</code></small>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          
          <div className="mt-4">
            <h6>Troubleshooting Tips</h6>
            <ul className="small">
              <li><strong>404 Not Found</strong> - The API endpoint doesn't exist or the URL is incorrect</li>
              <li><strong>CORS Issues</strong> - The API server isn't properly configured to allow cross-origin requests</li>
              <li><strong>Network Error</strong> - The API server may be down or unreachable</li>
              <li><strong>405 Method Not Allowed</strong> - The request method (GET, POST, etc.) is not supported for this endpoint</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiDebugView; 